import { calculatedArtworkDimensions } from "@design-lab/entities/artwork/lib/calculateDimensions";
import type { Artwork } from "@design-lab/entities/artwork/types";

export interface ArtworkApiResponse {
  id: number;
  currentEnhancementState?: string;
  original?: {
    imageUrl?: string;
  };
  ordered?: {
    imageUrl?: string;
    noBgUrl?: string;
    withBgUrl?: string;
    noBgNoTrimUrl?: string;
    manualEditUrl?: string;
    manualEditNoTrimUrl?: string;
    canRemoveBg?: boolean;
    removeBackground?: boolean;
    enhanceImage?: boolean;
    enhancementTier?: string;
    metadata?: {
      error?: string;
    };
  };
}

/**
 * Processes raw artwork API response into a consistent format
 */
export function processArtworkData(artwork: ArtworkApiResponse): Artwork {
  return {
    ...artwork.ordered,
    id: String(artwork.id),
    originalImageUrl: artwork.original?.imageUrl || artwork.ordered?.imageUrl || "",
    url: artwork.ordered?.noBgUrl || artwork.ordered?.imageUrl || "",
    withBgUrl:
      artwork.ordered?.withBgUrl || artwork.original?.imageUrl || artwork.ordered?.imageUrl || "",
    noBgNoTrimUrl: artwork.ordered?.noBgNoTrimUrl || artwork.ordered?.imageUrl || "",
    manualEditUrl: artwork.ordered?.manualEditUrl || null,
    manualEditNoTrimUrl: artwork.ordered?.manualEditNoTrimUrl || null,
    canRemoveBg: artwork.ordered?.canRemoveBg ?? true,
    removeBackground: artwork.ordered?.removeBackground ?? false,
    enhanceImage: artwork.ordered?.enhanceImage ?? true,
    enhancementTier: artwork.ordered?.enhancementTier || "full_enhancements"
  } as unknown as Artwork;
}

/**
 * Sets up artwork form data for a given side/placement
 */
export function setupArtworkForm(
  form: any,
  options: {
    artwork: Artwork;
    side: "front" | "back";
    cachedImage: any;
    composition: any;
  }
) {
  const { artwork, side, cachedImage, composition } = options;
  form.setValue("currentPlacement", side);

  if (side === "back") {
    const surface = composition?.surfaces.back;
    const dims = calculatedArtworkDimensions(cachedImage?.image, surface);

    form.setValue("back.size", {
      width: dims.maxWidthInches,
      height: dims.maxHeightInches
    });
    form.setValue("back.artworkId", artwork.id);
    form.setValue("info.back.enhancementTier", "full_enhancements");
    form.setValue("info.back.removeBackground", true);
  } else {
    // Handle front placement
    const frontSurface = composition?.surfaces.front;
    const dimsFront = calculatedArtworkDimensions(cachedImage?.image, frontSurface);

    form.setValue("front.size", {
      width: dimsFront.maxWidthInches,
      height: dimsFront.maxHeightInches
    });

    const chestSurface = composition?.surfaces.frontLeftChest;
    const dimsChest = calculatedArtworkDimensions(cachedImage?.image, chestSurface);

    form.setValue("frontLeftChest.size", {
      width: dimsChest.maxWidthInches,
      height: dimsChest.maxHeightInches
    });

    form.setValue("front.artworkId", artwork.id);
    form.setValue("frontLeftChest.artworkId", artwork.id);
    form.setValue("info.front.enhancementTier", "full_enhancements");
    form.setValue("info.front.removeBackground", true);
  }
}

/**
 * Sets up artwork state machine based on enhancement state
 */
export function setupArtworkMachine(machine: any, artwork: Artwork, enhancementState?: string) {
  if (enhancementState === "complete_processing") {
    // Enhancement is complete - render final artwork
    machine.send({
      type: "reset",
      targetState: "finished",
      context: {
        visitedStates: ["progress", "uploaded", "enhancing", "finished"],
        artworkId: Number(artwork.id),
        artwork
      }
    });
  } else {
    // Still processing - show original image and listen for websocket updates
    machine.send({
      type: "reset",
      context: {
        visitedStates: ["progress"],
        artworkId: Number(artwork.id),
        artwork,
        source: "design_library" as const,
        startTime: Date.now()
      }
    });

    // Transition to enhancing state to listen for websocket updates
    machine.send({ type: "enhancement.started" });
  }
}
